import { useState, useEffect } from "react";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Header from "../../components/Header";
import ReactPlayer from "react-player";
import { useTranslation } from "react-i18next";
import ErrorState from "../../components/dashboard/ErrorState";

const Calendar = () => {
  const { t } = useTranslation();

  const [, setCurrentEvents] = useState([]);
  const [, setAllAlerts] = useState([]);
  const [xyz, setXYZ] = useState([]);
  const [open, setOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventURL, setEventURL] = useState("");
  const [eventBuildingName, setEventBuildingName] = useState("");
  const [eventCameraLocation, setEventCameraLocation] = useState("");
  const [loadError, setLoadError] = useState(false);

  const handleClose = () => setOpen(false);

  const handleDateClick = (selected) => {
    const title = prompt(t("calendar.promptNewTitle"));
    const calendarApi = selected.view.calendar;
    calendarApi.unselect();

    if (title) {
      calendarApi.addEvent({
        id: `${selected.dateStr}-${title}`,
        title,
        start: selected.startStr,
        end: selected.endStr,
        allDay: selected.allDay,
      });
    }
  };

  const getAlerts = async () => {
    setLoadError(false);
    try {
      const response = await axios.get("/v1/api/all-alerts");

      const formattedEvents = response.data.map((event) => {
        let color = "#08764e";
        if (event.event_type === "Arson") color = "#b92d3d";
        else if (event.event_type === "Dumping") color = "#966000";
        return {
          title: event.event_type,
          start: event.event_date,
          color,
          extendedProps: { description: event.building_name, camera_loc: event.camera_loc },
        };
      });
      setXYZ(formattedEvents);
      setAllAlerts(response.data);
    } catch (_) {
      setLoadError(true);
    }
  };

  useEffect(() => {
    getAlerts();
  }, []);

  const handleEventClick = (selected) => {
    setEventDate(`${selected.event.start}`);
    setEventTitle(`${selected.event.title}`);
    setEventBuildingName(`${selected.event.extendedProps.description}`);
    setEventCameraLocation(`${selected.event.extendedProps.camera_loc}`);

    if (`${selected.event.title}` === "Arson") {
      setEventURL(
        "https://www.shutterstock.com/shutterstock/videos/1065539326/preview/stock-footage-a-man-is-pouring-fuel-over-plastic-garbage-a-homeless-man-burns-trash-in-an-iron-barrel.webm"
      );
    } else if (`${selected.event.title}` === "Dumping") {
      setEventURL(
        "https://www.shutterstock.com/shutterstock/videos/1078038722/preview/stock-footage-garbage-people-throw-out-garbage-full-bins-garbage-lying-on-the-streets-of-the-city-people-walk.webm"
      );
    } else {
      setEventURL(
        "https://media.istockphoto.com/id/514482433/video/parking-lot-car-accident.mp4?s=mp4-640x640-is&k=20&c=gQhuSP5sJ2n_0s-px-s7m-WGp4O2L335SNKINdHVS6M="
      );
    }

    setOpen(true);
  };

  return (
    <Box m="20px">
      <Header title={t("calendar.title")} subtitle={t("calendar.subtitle")} />
      {loadError && <ErrorState compact onRetry={getAlerts} />}

      <Box display="flex" justifyContent="space-between">
        <Box flex="1 1 100%" minWidth={0}>
          <FullCalendar
            height="75vh"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
            }}
            initialView="dayGridMonth"
            editable
            selectable
            selectMirror
            dayMaxEvents
            select={handleDateClick}
            eventClick={handleEventClick}
            eventsSet={(events) => setCurrentEvents(events)}
            events={xyz}
          />
        </Box>
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle
          sx={{
            color: "success.dark",
            display: "inline",
            fontWeight: "bold",
            mx: 0.5,
            fontSize: 14,
          }}
        >
          {eventDate}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              color: "error.dark",
              display: "inline",
              fontWeight: "bold",
              mx: 0.1,
              fontSize: 14,
            }}
          >
            {eventTitle}
          </DialogContentText>
        </DialogContent>

        <DialogContent>
          <DialogContentText
            sx={{
              color: "text.primary",
              display: "inline",
              fontWeight: "bold",
              mx: 0.1,
              fontSize: 14,
            }}
          >
            {eventBuildingName + " ( " + eventCameraLocation + " ) "}
          </DialogContentText>
        </DialogContent>

        <ReactPlayer
          width={"100%"}
          height="100%"
          url={eventURL}
          playing={true}
          volume={true}
          pip={false}
          controls
          loop
          muted={true}
          onError={() => console.log("onError callback")}
        />

        <DialogActions>
          <Button onClick={handleClose} color="warning">
            {t("calendar.dialog.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calendar;
